import { useState, useEffect } from "react";
import {
  Card,
  Title,
  Button,
  Flex,
  Text,
  Divider,
  Callout,
} from "@tremor/react";
import DevicesTable from "./table";
import DeviceDataTable from "../../components/DeviceDataTable";
import DeviceStatsCards from "../../components/DeviceStatsCards";
import Nav from "../../components/nav";
import * as devicesAPI from "../../services/device";
import AddDevice from "./add";
import { CpuChipIcon, PlusCircleIcon, TableCellsIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

// Skeleton for simple devices table
function DevicesTableSkeleton() {
  return (
    <div className="animate-pulse overflow-x-auto">
      {/* Table Header */}
      <div className="flex items-center p-4 border-b border-gray-200">
        <div className="flex-1 grid grid-cols-4 gap-4">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded hidden sm:block"></div>
          <div className="h-4 bg-gray-200 rounded hidden md:block"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
      
      {/* Table Rows */}
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex items-center p-4 border-b border-gray-100">
          <div className="flex-1 grid grid-cols-4 gap-4 items-center">
            {/* Serial */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-100 rounded w-1/2 sm:hidden"></div>
            </div>
            
            {/* Name */}
            <div className="h-4 bg-gray-200 rounded w-2/3 hidden sm:block"></div>
            
            {/* Type */}
            <div className="h-6 bg-gray-200 rounded w-16 hidden md:block"></div>
            
            {/* Last Seen */}
            <div className="space-y-1">
              <div className="h-4 bg-gray-200 rounded w-20"></div>
              <div className="h-4 bg-gray-100 rounded w-12 md:hidden"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Skeleton for data view table
function DeviceDataTableSkeleton() {
  return (
    <div className="animate-pulse overflow-x-auto">
      {/* Table Header */}
      <div className="flex items-center p-4 border-b border-gray-200">
        <div className="flex-1 grid grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="h-4 bg-gray-200 rounded min-w-[200px]"></div>
          <div className="h-4 bg-gray-200 rounded min-w-[100px]"></div>
          <div className="h-4 bg-gray-200 rounded min-w-[300px]"></div>
          <div className="h-4 bg-gray-200 rounded min-w-[150px] hidden lg:block"></div>
        </div>
      </div>
      
      {/* Table Rows */}
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex items-center p-4 border-b border-gray-100">
          <div className="flex-1 grid grid-cols-3 lg:grid-cols-4 gap-4 items-center">
            {/* Device */}
            <div className="space-y-2 min-w-[200px]">
              <div className="h-5 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-100 rounded w-1/2"></div>
              <div className="h-5 bg-gray-200 rounded w-16"></div>
            </div>
            
            {/* Status */}
            <div className="space-y-2 min-w-[100px]">
              <div className="h-5 bg-gray-200 rounded w-16"></div>
              <div className="h-3 bg-gray-100 rounded w-20"></div>
            </div>
            
            {/* Latest Values */}
            <div className="space-y-2 min-w-[300px]">
              <div className="flex gap-2">
                <div className="h-6 bg-gray-200 rounded w-20"></div>
                <div className="h-6 bg-gray-200 rounded w-24"></div>
                <div className="h-6 bg-gray-200 rounded w-16"></div>
                <div className="h-6 bg-gray-200 rounded w-18"></div>
              </div>
              <div className="h-4 bg-gray-100 rounded w-32 lg:hidden"></div>
            </div>
            
            {/* Activity */}
            <div className="space-y-2 min-w-[150px] hidden lg:block">
              <div className="h-4 bg-gray-200 rounded w-32"></div>
              <div className="h-3 bg-gray-100 rounded w-20"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const EmptyDevice = function ({ open }) {
  return (
    <>
      <div className="mt-4 flex h-52 items-center justify-center rounded-tremor-small border border-dashed border-tremor-border p-4 dark:border-dark-tremor-border">
        <div className="text-center">
          <CpuChipIcon
            className="mx-auto h-7 w-7 text-tremor-content-subtle dark:text-dark-tremor-content-subtle"
            aria-hidden={true}
          />

          <p className="mt-2 text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            No Devices available
          </p>
          <p className="text-tremor-default text-tremor-content dark:text-dark-tremor-content">
            Get started by connecting a device
          </p>
          <Button
            icon={PlusCircleIcon}
            onClick={open}
            type="button"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-tremor-small bg-tremor-brand px-3 py-2 text-tremor-default font-medium text-tremor-brand-inverted shadow-tremor-input hover:bg-tremor-brand-emphasis"
          >
            Add Device
          </Button>
        </div>
      </div>
    </>
  );
};

export default function Devices() {
  let [isOpen, setIsOpen] = useState(false);
  let [devices, setDevices] = useState([]);
  let [error, setError] = useState(null);
  let [viewMode, setViewMode] = useState('data'); // 'data' or 'simple'
  let [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const getDevices = async () => {
      setLoading(true);
      try {
        const data = await devicesAPI.fetchDevices();
        setDevices(data);
      } catch (err) {
        console.error("Error fetching devices:", err);
      } finally {
        setLoading(false);
      }
    };

    getDevices();
  }, []);

  const initDeviceForm = () => {
    setIsOpen(true);
  };

  const onAddDevice = async (device) => {
    try {
      const newDevice = await devicesAPI.addDevice(device);
      setDevices([...devices, newDevice]); // Add the new device to the list
      setIsOpen(false); // Close the form/modal
      // Redirect to device overview page
      navigate(`/devices/${newDevice._id}`);
    } catch (err) {
      const errMsg = err.message || "Failed to add device";
      setError(errMsg);
      setTimeout(() => {
        setError(null);
      }, 3000);
      setIsOpen(false); // Close the form/modal
    }
  };

  return (
    <div>
      <Nav />
      <main className="p-4 md:p-10 mx-auto max-w-7xl">
        <Flex className="flex-col sm:flex-row gap-4 sm:gap-0">
          <div className="flex-1">
            <Title>Devices</Title>
            <Text>Devices available to connect to the network.</Text>
          </div>
          <div className="flex-shrink-0 flex gap-2">
            <div className="flex border border-tremor-border rounded">
              <Button
                variant={viewMode === 'data' ? 'primary' : 'secondary'}
                size="xs"
                onClick={() => setViewMode('data')}
                icon={ChartBarIcon}
                className="rounded-r-none border-r-0"
              >
                Data View
              </Button>
              <Button
                variant={viewMode === 'simple' ? 'primary' : 'secondary'}
                size="xs"
                onClick={() => setViewMode('simple')}
                icon={TableCellsIcon}
                className="rounded-l-none"
              >
                Simple
              </Button>
            </div>
            <Button variant="primary" size="xs" onClick={initDeviceForm} className="w-full sm:w-auto">
              Add Device
            </Button>
          </div>
        </Flex>
        <Divider />
        
        {/* Stats Cards - only show in data view and when there are devices */}
        {viewMode === 'data' && devices.length > 0 && (
          <DeviceStatsCards devices={devices} />
        )}
        
        <Card className="mt-6">
          {error && (
            <Callout
              title="Error: failed to add device."
              color="red"
              className="mb-2"
            >
              {error}
            </Callout>
          )}
          {loading ? (
            viewMode === 'data' ? (
              <DeviceDataTableSkeleton />
            ) : (
              <DevicesTableSkeleton />
            )
          ) : devices.length > 0 ? (
            viewMode === 'data' ? (
              <DeviceDataTable devices={devices} />
            ) : (
              <DevicesTable devices={devices} />
            )
          ) : (
            <EmptyDevice open={setIsOpen} />
          )}
        </Card>
        {isOpen && <AddDevice onCancel={setIsOpen} onAction={onAddDevice} />}
      </main>
    </div>
  );
}
